import { Call, RpcProvider } from 'starknet';
import { getConfigManager } from '../../config';
import { DEFAULT_DECIMAL_PRECISION, Decimal } from '../../datastructures';
import { Asset, Nullable } from '../../interfaces';
import { getAccountAddressFromId, getNostraTokenFromFlags } from '../../utils';
import { MultiCallContract, getAccountService } from '../../services';
import { HEALTH_FACTOR_INFINITY, MULTICALL_CONTRACT_ADDRESS, POLLING_INTERVAL_IN_MS } from '../../constants';
import { walletAddressAtom } from '../wallet';
import accountsAtom from './accountsAtom';
import { NostraTokenBalanceMap } from './nostraTokenBalanceAtom';
import { TokenRateMap } from './assetTokenRatesAtom';
import { AssetVariantMap } from './assetVariantAtom';
import { createAsyncAtom } from '../../lib/state';
import rpcProviderAtom from '../rpcProviderAtom';

interface AccountHealthData {
  value: Decimal;
  totalCollateral: Decimal;
  totalDebt: Decimal;
  walletAddress: string;
  accountId: number;
  accountName: string;
}

export interface AccountHealthFactorMap {
  [walletAddressAccountId: string]: AccountHealthData;
}

const PRECISION_OFFSET = 0.0005;
const RESPONSE_CHUNK_SIZE = 8;

const fetchData = async (
  address: string,
  accountIds: number[],
  provider: RpcProvider,
): Promise<Nullable<AccountHealthFactorMap>> => {
  try {
    const cdpManagerAddress = getConfigManager().getConfig().cdpManager;
    const accountService = getAccountService();

    let result: Nullable<bigint[]> = null;
    try {
      const calldata: Call[] = accountIds.map(accountId => ({
        contractAddress: cdpManagerAddress,
        entrypoint: 'user_account_data',
        calldata: [getAccountAddressFromId(address, accountId)],
      }));

      const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

      result = await multiCallContract.aggregate(calldata);
    } catch (error) {
      console.warn('Failed to getUserAccountData with v2 contract, trying v1');

      const calldata: Call[] = accountIds.map(accountId => ({
        contractAddress: cdpManagerAddress,
        entrypoint: 'getUserAccountData',
        calldata: [getAccountAddressFromId(address, accountId)],
      }));

      const multiCallContract = new MultiCallContract(MULTICALL_CONTRACT_ADDRESS, provider);

      result = await multiCallContract.aggregate(calldata);
    }
    if (!result) {
      return null;
    }

    const healthFactorMap: AccountHealthFactorMap = {};

    for (let i = 0; i < result.length; i += RESPONSE_CHUNK_SIZE) {
      // Index 1,2 is totalCollateral
      // Index 3,4 is totalDebt
      // Index 5 is isValidDebt
      // Index 6,7 is healthFactor
      const totalCollateral = new Decimal({ low: result[i + 1], high: result[i + 2] }, DEFAULT_DECIMAL_PRECISION);
      const totalDebt = new Decimal({ low: result[i + 3], high: result[i + 4] }, DEFAULT_DECIMAL_PRECISION);
      const healthFactor = new Decimal({ low: result[i + 6], high: result[i + 7] }, DEFAULT_DECIMAL_PRECISION);
      const accountId = accountIds[i / RESPONSE_CHUNK_SIZE];

      healthFactorMap[`${address}-${accountId}`] = {
        totalCollateral,
        totalDebt,
        value: healthFactor,
        walletAddress: address,
        accountId,
        accountName: accountService.getAccountName(address, accountId),
      };
    }

    return healthFactorMap;
  } catch (error) {
    console.error(`useAccountHealthFactor - failed to fetch health factors for '${address}'`, error);
    return null;
  }
};

const calculateHealthFactor: (totalCollateral: Decimal, totalDebt: Decimal) => Decimal = (
  totalCollateral,
  totalDebt,
) => {
  const healthFactor = totalDebt.isZero() ? new Decimal(HEALTH_FACTOR_INFINITY) : totalCollateral.div(totalDebt);

  if (healthFactor.lt(0)) {
    return Decimal.ZERO;
  }

  if (healthFactor.gte(HEALTH_FACTOR_INFINITY)) {
    return Decimal.MAX_DECIMAL;
  }

  return healthFactor;
};

// we are using different math lib between frontend and smart contract, it's possible to have precision error between total collateral
// use this function only for displaying number on frontend
export const calculateTotalCollateral = (
  walletAddress: string,
  accountId: number,
  assets: Asset[],
  nostraTokenBalances: NostraTokenBalanceMap,
  tokenRatesInBaseAsset: TokenRateMap,
) =>
  assets.reduce((sum, item) => {
    const nostraInterestBearingCollateralTokenDataKey = `${walletAddress}-${accountId}-${item.nostraInterestBearingCollateralTokenAddress}`;
    const nostraCollateralTokenDataKey = `${walletAddress}-${accountId}-${item.nostraCollateralTokenAddress}`;

    const nostraInterestBearingCollateralTokenBalance =
      nostraTokenBalances[nostraInterestBearingCollateralTokenDataKey];
    const nostraCollateralTokenBalance = nostraTokenBalances[nostraCollateralTokenDataKey];

    if (
      !sum ||
      !nostraInterestBearingCollateralTokenBalance ||
      !nostraCollateralTokenBalance ||
      !tokenRatesInBaseAsset[item.address] ||
      !item.collateralFactor
    ) {
      return null;
    }

    const totalNostraTokenBalance = nostraInterestBearingCollateralTokenBalance.add(nostraCollateralTokenBalance);
    const totalNostraTokenAmountInEth = totalNostraTokenBalance.mul(tokenRatesInBaseAsset[item.address]);

    return sum.add(totalNostraTokenAmountInEth.mul(item.collateralFactor));
  }, Decimal.ZERO as Nullable<Decimal>);

// we are using different math lib between frontend and smart contract, it's possible to have precision error between total debt
// use this function only for displaying number on frontend
export const calculateTotalDebt = (
  walletAddress: string,
  accountId: number,
  assets: Asset[],
  nostraTokenBalances: NostraTokenBalanceMap,
  tokenRatesInBaseAsset: TokenRateMap,
) => {
  return assets.reduce((sum, item) => {
    const debtTokenDataKey = `${walletAddress}-${accountId}-${item.debtTokenAddress}`;
    const debtTokenBalance = nostraTokenBalances[debtTokenDataKey];

    if (!sum || !debtTokenBalance || !tokenRatesInBaseAsset[item.address] || !item.debtFactor) {
      return null;
    }

    const totalDebtTokenAmountInEth = debtTokenBalance.mul(tokenRatesInBaseAsset[item.address]);

    return sum.add(totalDebtTokenAmountInEth.div(item.debtFactor));
  }, Decimal.ZERO as Nullable<Decimal>);
};

const calculateNewHealthFactor = (
  totalCollateralInBaseAsset: Decimal,
  totalDebtInBaseAsset: Decimal,
  collateralAsset: Asset,
  debtAsset: Asset,
  collateralChange: Decimal,
  debtChange: Decimal,
  tokenRatesInBaseAsset: TokenRateMap,
) => {
  const collateralTokenPriceInBaseAsset = tokenRatesInBaseAsset[collateralAsset.address] ?? Decimal.ZERO;
  const debtTokenPriceInBaseAsset = tokenRatesInBaseAsset[debtAsset.address] ?? Decimal.ZERO;

  const newCollateral = collateralChange
    .mul(collateralTokenPriceInBaseAsset)
    .mul(collateralAsset.collateralFactor ?? Decimal.ZERO);
  const newDebt = debtChange.mul(debtTokenPriceInBaseAsset).div(debtAsset.debtFactor ?? Decimal.MAX_DECIMAL);

  const newTotalCollateral = totalCollateralInBaseAsset.add(newCollateral);
  const newTotalDebt =
    newDebt.lt(0) && totalDebtInBaseAsset.sub(newDebt.abs()).lt(PRECISION_OFFSET)
      ? Decimal.ZERO
      : totalDebtInBaseAsset.add(newDebt);

  return calculateHealthFactor(newTotalCollateral, newTotalDebt);
};

const calculateNewDepositHealthFactor = (
  totalCollateralInBaseAsset: Decimal,
  totalDebtInBaseAsset: Decimal,
  asset: Asset,
  amount: Decimal,
  tokenRatesInBaseAsset: TokenRateMap,
) =>
  calculateNewHealthFactor(
    totalCollateralInBaseAsset,
    totalDebtInBaseAsset,
    asset,
    asset,
    amount,
    Decimal.ZERO,
    tokenRatesInBaseAsset,
  );

const calculateNewWithdrawHealthFactor = (
  totalCollateralInBaseAsset: Decimal,
  totalDebtInBaseAsset: Decimal,
  asset: Asset,
  amount: Decimal,
  tokenRatesInBaseAsset: TokenRateMap,
) =>
  calculateNewHealthFactor(
    totalCollateralInBaseAsset,
    totalDebtInBaseAsset,
    asset,
    asset,
    Decimal.ZERO.sub(amount),
    Decimal.ZERO,
    tokenRatesInBaseAsset,
  );

const calculateNewBorrowHealthFactor = (
  totalCollateralInBaseAsset: Decimal,
  totalDebtInBaseAsset: Decimal,
  asset: Asset,
  amount: Decimal,
  tokenRatesInBaseAsset: TokenRateMap,
) =>
  calculateNewHealthFactor(
    totalCollateralInBaseAsset,
    totalDebtInBaseAsset,
    asset,
    asset,
    Decimal.ZERO,
    amount,
    tokenRatesInBaseAsset,
  );

const calculateNewRepayHealthFactor = (
  totalCollateralInBaseAsset: Decimal,
  totalDebtInBaseAsset: Decimal,
  asset: Asset,
  amount: Decimal,
  tokenRatesInBaseAsset: TokenRateMap,
) =>
  calculateNewHealthFactor(
    totalCollateralInBaseAsset,
    totalDebtInBaseAsset,
    asset,
    asset,
    Decimal.ZERO,
    Decimal.ZERO.sub(amount),
    tokenRatesInBaseAsset,
  );

const calculateNewRepayWithCollateralHealthFactor = (
  totalCollateralInBaseAsset: Decimal,
  totalDebtInBaseAsset: Decimal,
  collateralAsset: Asset,
  debtAsset: Asset,
  collateralAmount: Decimal,
  debtAmount: Decimal,
  tokenRatesInBaseAsset: TokenRateMap,
) =>
  calculateNewHealthFactor(
    totalCollateralInBaseAsset,
    totalDebtInBaseAsset,
    collateralAsset,
    debtAsset,
    Decimal.ZERO.sub(collateralAmount),
    Decimal.ZERO.sub(debtAmount),
    tokenRatesInBaseAsset,
  );

const calculateNewAdjustHealthFactor = (
  totalCollateralInBaseAsset: Decimal,
  totalDebtInBaseAsset: Decimal,
  asset: Asset,
  balance: Decimal,
  isCollateral: boolean,
  tokenRatesInBaseAsset: TokenRateMap,
) =>
  calculateNewHealthFactor(
    totalCollateralInBaseAsset,
    totalDebtInBaseAsset,
    asset,
    asset,
    isCollateral ? balance : Decimal.ZERO.sub(balance),
    Decimal.ZERO,
    tokenRatesInBaseAsset,
  );

const calculateMaxWithdraw = (
  walletAddress: string,
  accountId: number,
  asset: Asset,
  healthFactor: Decimal,
  healthFactorBuffer: Decimal,
  healthFactorData: AccountHealthFactorMap,
  tokenRatesInBaseAsset: TokenRateMap,
  assetVariantMap: AssetVariantMap,
  nostraTokenBalances: NostraTokenBalanceMap,
) => {
  const dataKey = `${walletAddress}-${accountId}`;

  if (
    !healthFactorData[dataKey]?.totalCollateral ||
    !healthFactorData[dataKey]?.totalDebt ||
    !tokenRatesInBaseAsset[asset.address] ||
    !asset.collateralFactor ||
    !assetVariantMap[`${walletAddress}-${accountId}-${asset.address}`]
  ) {
    return null;
  }

  if (tokenRatesInBaseAsset[asset.address].isZero() || asset.collateralFactor.isZero()) {
    return Decimal.MAX_DECIMAL;
  }

  // In case user does not have any debt, we just return nostra token balance as max withdrawable amount
  if (healthFactorData[dataKey].totalDebt.isZero()) {
    const assetVariant = assetVariantMap[`${walletAddress}-${accountId}-${asset.address}`];

    const nostraToken = getNostraTokenFromFlags(asset, {
      collateral: assetVariant.isCollateral,
      interestBearing: assetVariant.isLending,
    });

    return nostraTokenBalances[`${walletAddress}-${accountId}-${nostraToken.address}`] || null;
  }

  return healthFactorData[dataKey].totalCollateral
    .mul(Decimal.ONE.sub(healthFactorBuffer))
    .sub(healthFactor.mul(healthFactorData[dataKey].totalDebt))
    .div(tokenRatesInBaseAsset[asset.address])
    .div(asset.collateralFactor);
};

const calculateMaxBorrow = (
  walletAddress: string,
  accountId: number,
  asset: Asset,
  healthFactor: Decimal,
  healthFactorBuffer: Decimal,
  healthFactorData: AccountHealthFactorMap,
  tokenRatesInBaseAsset: TokenRateMap,
) => {
  const dataKey = `${walletAddress}-${accountId}`;

  if (
    !healthFactorData[dataKey]?.totalCollateral ||
    !healthFactorData[dataKey]?.totalDebt ||
    !tokenRatesInBaseAsset[asset.address] ||
    !asset.debtFactor
  ) {
    return null;
  }

  if (tokenRatesInBaseAsset[asset.address].isZero()) {
    return Decimal.MAX_DECIMAL;
  }

  return healthFactorData[dataKey].totalCollateral
    .mul(Decimal.ONE.sub(healthFactorBuffer))
    .sub(healthFactor.mul(healthFactorData[dataKey].totalDebt))
    .div(tokenRatesInBaseAsset[asset.address])
    .mul(asset.debtFactor);
};

const accountsHealthFactorAtom = createAsyncAtom<AccountHealthFactorMap>(
  async get => {
    const walletAddress = await get(walletAddressAtom);
    const accounts = await get(accountsAtom);
    const provider = await get(rpcProviderAtom);
    if (!walletAddress) {
      return {};
    }

    const accountIds = accounts
      .map((account, id) => [account, id] as const)
      .filter(([accountName]) => Boolean(accountName))
      .map(([, id]) => id);

    const result = await fetchData(walletAddress, accountIds, provider);

    if (!result) {
      return {};
    }

    return result;
  },
  POLLING_INTERVAL_IN_MS,
  [
    'lend-and-borrow-deposit',
    'lend-and-borrow-withdraw',
    'lend-and-borrow-borrow',
    'lend-and-borrow-repay',
    'lend-and-borrow-adjust',
    'unstake-and-deposit',
    'user-refresh',
  ],
);

export {
  calculateNewDepositHealthFactor,
  calculateNewWithdrawHealthFactor,
  calculateNewBorrowHealthFactor,
  calculateNewRepayHealthFactor,
  calculateNewRepayWithCollateralHealthFactor,
  calculateNewAdjustHealthFactor,
  calculateMaxWithdraw,
  calculateMaxBorrow,
};

export default accountsHealthFactorAtom;
